import { Tooltip } from "antd";

/**
 * `additional_phone` holds any number of alternate numbers.
 *
 * The API accepts an array or a CSV string and always stores CSV, so every
 * screen has to be able to read a CSV back — hence one parser shared by all of
 * them instead of each list doing its own `.split(",")`.
 */
export const parsePhones = (value) => {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : String(value).split(",");
  const seen = new Set();
  return raw
    .map((v) => String(v ?? "").trim())
    .filter((v) => {
      if (!v || seen.has(v)) return false;   // drop blanks and duplicates
      seen.add(v);
      return true;
    });
};

/** Normalized value to send back to the API. */
export const phonesPayload = (list) => parsePhones(list);

/** "9876543210, 8765432109" — for exports and tooltips. */
export const phonesText = (value) => parsePhones(value).join(", ");

/**
 * Renders the alternate numbers without ever growing the row/card: the first
 * `max` are shown inline, the rest collapse into a "+N" chip that reveals them
 * on hover.
 */
export const AdditionalPhones = ({
  value,
  max = 2,
  style = {},
  itemStyle = {},
  prefix = "📱",
  dot = false,          // blue bullet instead of a text prefix (list tables)
}) => {
  const list = parsePhones(value);
  if (!list.length) return null;

  const shown = list.slice(0, max);
  const rest = list.slice(max);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", ...style }}>
      {shown.map((p) => (
        <span
          key={p}
          style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 600, color: "#6b7280", fontSize: 11, ...itemStyle }}
        >
          {dot
            ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", flexShrink: 0 }} />
            : prefix}
          {p}
        </span>
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.join(", ")}>
          <span
            style={{
              background: "#f3f4f6", color: "#4b5563", border: "1px solid #e5e7eb",
              borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700,
              cursor: "default", whiteSpace: "nowrap",
            }}
          >
            +{rest.length}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default AdditionalPhones;

/**
 * Leads are global now — the number alone is ambiguous, so it is always shown
 * with its country code: "+91 9876543210".
 */
export const withCountryCode = (code, phone) => {
  const num = String(phone ?? "").trim();
  if (!num) return "";
  const cc = String(code ?? "").trim();
  if (!cc) return num;
  return `${cc.startsWith("+") ? cc : `+${cc}`} ${num}`;
};

/** Digits-only E.164-ish number for the dialer: "+919876543210" → 919876543210 */
export const dialNumber = (code, phone) => {
  const num = String(phone ?? "").replace(/\D/g, "");
  const cc = String(code ?? "").replace(/\D/g, "");
  if (!num) return "";
  // A number already carrying its country code must not get it twice.
  return cc && !num.startsWith(cc) ? `${cc}${num}` : num;
};

/** Country codes offered when a lead is created by hand. */
export const COUNTRY_CODES = [
  { code: "+91",  flag: "🇮🇳", label: "India" },
  { code: "+1",   flag: "🇺🇸", label: "USA / Canada" },
  { code: "+44",  flag: "🇬🇧", label: "United Kingdom" },
  { code: "+971", flag: "🇦🇪", label: "UAE" },
  { code: "+61",  flag: "🇦🇺", label: "Australia" },
  { code: "+65",  flag: "🇸🇬", label: "Singapore" },
  { code: "+49",  flag: "🇩🇪", label: "Germany" },
  { code: "+33",  flag: "🇫🇷", label: "France" },
  { code: "+81",  flag: "🇯🇵", label: "Japan" },
  { code: "+86",  flag: "🇨🇳", label: "China" },
  { code: "+7",   flag: "🇷🇺", label: "Russia" },
  { code: "+27",  flag: "🇿🇦", label: "South Africa" },
  { code: "+55",  flag: "🇧🇷", label: "Brazil" },
  { code: "+92",  flag: "🇵🇰", label: "Pakistan" },
  { code: "+880", flag: "🇧🇩", label: "Bangladesh" },
  { code: "+977", flag: "🇳🇵", label: "Nepal" },
  { code: "+94",  flag: "🇱🇰", label: "Sri Lanka" },
  { code: "+60",  flag: "🇲🇾", label: "Malaysia" },
  { code: "+64",  flag: "🇳🇿", label: "New Zealand" },
  { code: "+966", flag: "🇸🇦", label: "Saudi Arabia" },
];

/** 4–15 digits — E.164 allows up to 15, so no India-only 10-digit rule. */
export const isValidPhone = (v) => /^\d{4,15}$/.test(String(v ?? "").trim());
